/**
 * @fileoverview Toast global de conversas sem resposta há +10min.
 *
 * O alerta é PESSOAL: conta só as conversas atribuídas a quem recebe, para
 * qualquer perfil (admin incluído). O backend decide QUANDO notificar (escalas
 * 1/5/10/15… por ciclo, sweep de 1min) e emite `user-<id>-noResponseAlert` na
 * sala do próprio usuário; aqui só exibimos. O guard em localStorage
 * (compartilhado entre abas, sobrevive a refresh/reconexão) impede repetir uma
 * escala já mostrada dentro do mesmo ciclo — um cycleId novo invalida o guard
 * sozinho; a chave inclui o userId porque o mesmo navegador pode trocar de
 * conta. A ação abre o mesmo modal do indicador (NoResponseOffendersModal),
 * filtrado no próprio usuário — a listagem bate com a contagem notificada.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MessageCircleWarning } from "lucide-react";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import NoResponseOffendersModal from "../NoResponseOffendersModal";

const TOAST_ID = "no-response-alert";
const AUTO_CLOSE_MS = 3500;

const storageKey = (companyId, userId) => `noResponseAlert:${companyId}:${userId}`;

const readGuard = (companyId, userId) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(companyId, userId)) || "null");
  } catch (err) {
    return null;
  }
};

const writeGuard = (companyId, userId, cycleId, maxScale) => {
  try {
    localStorage.setItem(
      storageKey(companyId, userId),
      JSON.stringify({ cycleId, maxScale })
    );
  } catch (err) {
    // Sem localStorage o pior caso é repetir o toast num refresh.
  }
};

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "10px 4px",
  },
  icon: {
    color: "#fff",
    flexShrink: 0,
    // Compensa o leading do line-height pra cravar no topo da 1ª linha
    marginTop: 2,
  },
  message: {
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.5,
    color: "#fff",
    marginBottom: 10,
  },
  actionBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12.5,
    borderRadius: 8,
    padding: "3px 14px",
    color: "#fff",
    border: "2px solid #fff",
    "&:hover": {
      border: "2px solid #fff",
      background: "rgba(255, 255, 255, 0.12)",
    },
  },
  // Card vermelho de alerta (aprovado pelo usuário): sobrepõe o visual default
  // do react-toastify só para este toast.
  "@global": {
    ".no-response-alert-toast": {
      background: "#b01212 !important",
      color: "#fff !important",
      borderRadius: "10px !important",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35) !important",
    },
    ".no-response-alert-toast .Toastify__close-button": {
      color: "#fff",
      opacity: 0.85,
      alignSelf: "flex-start",
    },
  },
}));

const AlertToastContent = ({ count, onView }) => {
  const classes = useStyles();
  const message =
    count === 1
      ? i18n.t("noResponseAlert.messageOne")
      : i18n.t("noResponseAlert.messageMany", { count });

  return (
    <div className={classes.container}>
      <MessageCircleWarning size={32} className={classes.icon} aria-label="alerta" />
      <div>
        <div className={classes.message}>{message}</div>
        <Button
          size="small"
          variant="outlined"
          className={classes.actionBtn}
          onClick={onView}
        >
          {i18n.t("noResponseAlert.action")}
        </Button>
      </div>
    </div>
  );
};

const NoResponseAlertToast = () => {
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const [modalOpen, setModalOpen] = useState(false);
  // Timer próprio de auto-close: o autoClose nativo do react-toastify 9.0.0
  // não dispara com esta combinação de props (bug conhecido da lib).
  const dismissTimerRef = useRef(null);

  const companyId = user?.companyId;
  const userId = user?.id;
  const isInternalOnly = user?.accessScope === "internal";

  const handleView = useCallback(() => {
    toast.dismiss(TOAST_ID);
    setModalOpen(true);
  }, []);

  const maybeShow = useCallback(
    (payload) => {
      if (!payload?.cycleId || !(payload.count > 0)) return;

      const guard = readGuard(companyId, userId);
      if (
        guard &&
        guard.cycleId === payload.cycleId &&
        guard.maxScale >= payload.scale
      ) {
        return;
      }

      const content = <AlertToastContent count={payload.count} onView={handleView} />;
      if (toast.isActive(TOAST_ID)) {
        toast.update(TOAST_ID, { render: content });
      } else {
        toast(content, {
          toastId: TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          position: "top-right",
          className: "no-response-alert-toast",
        });
      }
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => toast.dismiss(TOAST_ID), AUTO_CLOSE_MS);
      writeGuard(companyId, userId, payload.cycleId, payload.scale);
    },
    [companyId, userId, handleView]
  );

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  // Sincronização com o estado atual do ciclo. Roda no mount (login/refresh)
  // e a cada "ready" do socket (reconexão) — eventos emitidos enquanto o
  // cliente estava desconectado são perdidos pelo socket.io, então o refetch
  // é o que ressincroniza; o guard do localStorage evita duplicar exibição.
  const fetchState = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/no-response-alert-state");
      if (data?.enabled) maybeShow(data);
    } catch (err) {
      // Alerta é acessório: falha aqui não pode quebrar o layout.
    }
  }, [maybeShow]);

  useEffect(() => {
    if (!companyId || !userId || isInternalOnly) return;
    fetchState();
  }, [companyId, userId, isInternalOnly, fetchState]);

  useEffect(() => {
    if (!companyId || !userId || isInternalOnly) return;
    const socket = socketManager.getSocket(companyId);

    const handleAlert = (data) => {
      // O sweep só emite para empresas com o setting habilitado — não há o
      // que re-checar aqui (um gate local só criaria estado pra dessincronizar).
      if (data?.action === "alert") maybeShow(data);
      if (data?.action === "clear") toast.dismiss(TOAST_ID);
    };

    const handleSettings = (data) => {
      if (data?.action === "update" && data.setting?.key === "noResponseIndicator") {
        if (data.setting.value !== "enabled") {
          toast.dismiss(TOAST_ID);
          setModalOpen(false);
        }
      }
    };

    // Canal pessoal: o backend emite na sala `user-<id>`, então cada atendente
    // recebe só o seu número (o de settings continua sendo da empresa).
    socket.on(`user-${userId}-noResponseAlert`, handleAlert);
    socket.on(`company-${companyId}-settings`, handleSettings);
    socket.on("ready", fetchState);

    return () => {
      socket.off(`user-${userId}-noResponseAlert`, handleAlert);
      socket.off(`company-${companyId}-settings`, handleSettings);
      socket.off("ready", fetchState);
    };
  }, [companyId, userId, isInternalOnly, socketManager, maybeShow, fetchState]);

  // Renderização condicional (não só o prop open): desmontar ao fechar zera os
  // filtros internos do modal — a listagem reaberta volta sempre à janela
  // default, a mesma da contagem notificada.
  if (!modalOpen) return null;

  // Entrando pelo toast, o modal abre filtrado em quem clicou — inclusive
  // admin: o alerta é pessoal, então a listagem tem que bater com o número
  // notificado. A visão da empresa inteira continua disponível abrindo o modal
  // pelo Dashboard/indicador, que não passa filtro inicial.
  return (
    <NoResponseOffendersModal
      open
      onClose={() => setModalOpen(false)}
      initialUserId={user?.id}
      initialUserName={user?.name}
    />
  );
};

export default NoResponseAlertToast;
